import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaBrain, 
  FaCloud, 
  FaRobot, 
  FaChartLine, 
  FaShieldAlt, 
  FaCogs,
  FaServer,
  FaDatabase,
  FaNetworkWired,
  FaLock,
  FaRocket,
  FaHeadset
} from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      icon: <FaBrain />,
      title: "Machine Learning Solutions",
      description: "Custom machine learning models and AI algorithms designed specifically for your business needs",
      features: [
        "Custom ML model development",
        "Deep learning solutions",
        "Natural language processing",
        "Computer vision applications",
        "Predictive analytics",
        "Model optimization and tuning"
      ],
      category: "AI/ML"
    },
    {
      icon: <FaCloud />,
      title: "Cloud Infrastructure",
      description: "Scalable cloud solutions with expertise in AWS, Azure, and Google Cloud platforms",
      features: [
        "Cloud architecture design",
        "Kubernetes deployment",
        "Serverless solutions",
        "Multi-cloud strategies",
        "Cost optimization",
        "High availability setup"
      ],
      category: "Cloud"
    },
    {
      icon: <FaRobot />,
      title: "AI Automation",
      description: "Intelligent automation systems to streamline operations and improve efficiency",
      features: [
        "Process automation",
        "Workflow optimization",
        "RPA implementation",
        "Intelligent document processing",
        "Chatbot development",
        "API integration"
      ],
      category: "Automation"
    },
    {
      icon: <FaChartLine />,
      title: "Data Analytics",
      description: "Advanced analytics and business intelligence solutions to drive data-driven decisions",
      features: [
        "Data pipeline development",
        "Real-time analytics",
        "Business intelligence dashboards",
        "Data visualization",
        "Statistical analysis",
        "Performance metrics"
      ],
      category: "Analytics"
    },
    {
      icon: <FaShieldAlt />,
      title: "AI Security",
      description: "Cybersecurity solutions powered by artificial intelligence to protect your digital assets",
      features: [
        "Threat detection",
        "Anomaly detection",
        "Security automation",
        "Compliance monitoring",
        "Incident response",
        "Security audits"
      ],
      category: "Security"
    },
    {
      icon: <FaCogs />,
      title: "System Integration",
      description: "Seamless integration of AI systems with existing infrastructure and applications",
      features: [
        "API development",
        "Legacy system integration",
        "Microservices architecture",
        "Data migration",
        "Third-party integrations",
        "System optimization"
      ],
      category: "Integration"
    }
  ];

  const additionalServices = [
    {
      icon: <FaServer />,
      title: "DevOps & CI/CD",
      description: "Streamlined development and deployment pipelines for faster delivery"
    },
    {
      icon: <FaDatabase />,
      title: "Data Engineering",
      description: "Robust data infrastructure and ETL processes for reliable data management"
    },
    {
      icon: <FaNetworkWired />,
      title: "IoT Solutions",
      description: "Connected device solutions with real-time data processing and analytics"
    },
    {
      icon: <FaLock />,
      title: "Compliance & Governance",
      description: "Ensure your AI solutions meet regulatory requirements and industry standards"
    },
    {
      icon: <FaRocket />,
      title: "Digital Transformation",
      description: "End-to-end digital transformation consulting and implementation"
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Support",
      description: "Round-the-clock technical support and maintenance for your AI systems"
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section text-white" style={{ minHeight: '50vh' }}>
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={8} className="mx-auto text-center hero-content">
              <h1 className="display-4 fw-bold mb-4">Our Services</h1>
              <p className="lead mb-5 fs-5">
                Comprehensive AI and cloud solutions designed to accelerate your digital transformation 
                and drive business growth through intelligent technology.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Services */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Core Services</h2>
              <p className="section-subtitle">
                Our flagship services that form the foundation of modern AI-powered businesses
              </p>
            </Col>
          </Row>
          <Row>
            {services.map((service, index) => (
              <Col lg={6} key={index} className="mb-5">
                <Card className="h-100 service-detail-card">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start mb-4">
                      <div className="service-icon me-3">
                        {service.icon}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Card.Title className="fw-bold mb-1">{service.title}</Card.Title>
                          <Badge bg="primary" className="ms-2">{service.category}</Badge>
                        </div>
                        <Card.Text className="text-muted mb-3">
                          {service.description}
                        </Card.Text>
                      </div>
                    </div>
                    <div className="row">
                      {service.features.map((feature, featureIndex) => (
                        <Col sm={6} key={featureIndex} className="mb-2">
                          <div className="d-flex align-items-center">
                            <div className="text-primary me-2">•</div>
                            <small>{feature}</small>
                          </div>
                        </Col>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Additional Services</h2>
              <p className="section-subtitle">
                Supporting services to ensure your AI initiatives are successful and sustainable
              </p>
            </Col>
          </Row>
          <Row>
            {additionalServices.map((service, index) => (
              <Col lg={4} md={6} key={index} className="mb-4">
                <Card className="h-100 text-center">
                  <Card.Body className="p-4">
                    <div className="service-icon mb-3">
                      {service.icon}
                    </div>
                    <Card.Title className="fw-bold mb-3">{service.title}</Card.Title>
                    <Card.Text className="text-muted">
                      {service.description}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Our Process</h2>
              <p className="section-subtitle">
                A proven methodology to ensure successful AI implementation and maximum ROI
              </p>
            </Col>
          </Row>
          <Row>
            <Col lg={3} md={6} className="mb-4">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}>
                  1
                </div>
                <h5 className="fw-bold">Discovery</h5>
                <p className="text-muted">Understand your business needs and objectives</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}>
                  2
                </div>
                <h5 className="fw-bold">Strategy</h5>
                <p className="text-muted">Develop a comprehensive AI strategy and roadmap</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}>
                  3
                </div>
                <h5 className="fw-bold">Implementation</h5>
                <p className="text-muted">Build and deploy your AI solutions</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="text-center">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}>
                  3
                </div>
                <h5 className="fw-bold">Optimization</h5>
                <p className="text-muted">Monitor, maintain, and continuously improve</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title text-white">Ready to Get Started?</h2>
              <p className="section-subtitle text-white-50">
                Let's discuss your specific needs and how our AI solutions can transform your business.
              </p>
              <Button 
                as={Link} 
                to="/contact" 
                variant="light" 
                size="lg" 
                className="px-4 py-3 fw-semibold"
              >
                Schedule a Free Consultation
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Services;